import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site-info';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">内容未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您访问的内容不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article, 4);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.description,
          datePublished: article.date,
          author: {
            '@type': 'Organization',
            name: article.source,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
          image: article.thumbnail,
          url: `${siteInfo.url}/article/${article.id}`,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - ${siteInfo.name}`,
          description: article.description,
          url: `${siteInfo.url}/article/${article.id}`,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: cat?.name || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video rounded-xl overflow-hidden mb-6">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                    {cat?.name || article.category}
                  </span>
                  <span className="px-3 py-1 bg-black/50 text-white text-xs rounded-full backdrop-blur-sm">
                    {article.duration}
                  </span>
                </div>
              </div>
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000
                  ? `${(article.views / 10000).toFixed(1)}万次观看`
                  : `${article.views}次观看`}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-yellow-500"></i>
                {article.rating} 分
              </span>
              <span>
                来源：{article.source}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="prose prose-sm max-w-none mb-8">
              <p className="text-sm text-foreground-700 leading-relaxed">
                {article.content}
              </p>
            </div>

            <div className="border-t border-background-200 pt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  查看更多 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>

              {relatedArticles.length === 0 ? (
                <p className="text-sm text-foreground-500">暂无相关推荐</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-play-fill text-base text-primary-500"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-xs font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-star-fill text-yellow-500"></i>
                            {rel.rating}
                          </span>
                          <span>{rel.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}